/**
 * AccountMappingRow (Phase 2, §7.5) — one discovered account inside a
 * `ConnectionCard`, with the "Mit vorhandenem Konto verknüpfen" action.
 *
 * A freshly discovered account is auto-created and mapped by EB's stable
 * `identification_hash`. This row lets the user either confirm that auto-mapping
 * (`merge_into_account_id = null`) or merge the discovered account into an
 * existing manual account (`merge_into_account_id = <id>`), which re-points its
 * transactions. Already-linked accounts render as read-only.
 */
import { useState } from 'react';
import { Link2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button.tsx';
import { Select, type SelectOption } from '../../../components/ui/Select.tsx';
import { AmountText } from '../../../components/ui/AmountText.tsx';
import { useToast } from '../../../context/ToastContext.tsx';
import { useAccounts } from '../../../lib/hooks/useAccounts.ts';
import { useLinkAccount } from '../../../lib/hooks/useConnectionMutations.ts';
import { ACCOUNT_TYPE } from '../../../lib/labels.ts';
import { formatDate } from '../../../lib/format.ts';
import type { ConnectionAccountOut } from '../../../lib/types.ts';
import { maskIban } from '../format.ts';

export function AccountMappingRow({ account }: { account: ConnectionAccountOut }) {
  const { toast } = useToast();
  const link = useLinkAccount();
  const manualAccounts = useAccounts().filter((a) => a.is_manual && a.id !== account.account_id);

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string | undefined>(undefined);

  const options: SelectOption[] = manualAccounts.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  function submit(mergeIntoId: number | null) {
    link.mutate(
      { accountId: account.account_id, body: { merge_into_account_id: mergeIntoId } },
      {
        onSuccess: () => {
          setOpen(false);
          setTarget(undefined);
          toast('Konto verknüpft.', { variant: 'success' });
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {account.name}
            </span>
            {account.is_linked ? (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                Verknüpft
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {ACCOUNT_TYPE[account.type]} · {maskIban(account.iban)}
          </p>
          <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
            {account.synced_through
              ? `Abgeglichen bis ${formatDate(account.synced_through)}`
              : 'Noch nicht abgeglichen'}
          </p>
          {account.last_error ? (
            <p className="mt-0.5 text-xs text-rose-600 dark:text-rose-400">{account.last_error}</p>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          {account.current_balance != null ? (
            <AmountText value={account.current_balance} className="text-sm" />
          ) : (
            <span className="text-sm tabular-nums text-slate-400 dark:text-slate-500">—</span>
          )}
        </div>
      </div>

      {open ? (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3 dark:border-slate-800/70">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            In welches vorhandene Konto sollen die Umsätze übernommen werden?
          </p>
          {options.length > 0 ? (
            <Select
              value={target}
              onValueChange={setTarget}
              options={options}
              placeholder="Vorhandenes Konto wählen …"
            />
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Kein manuelles Konto zum Verknüpfen vorhanden.
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={link.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => submit(null)}
              disabled={link.isPending}
            >
              Zuordnung übernehmen
            </Button>
            <Button
              size="sm"
              onClick={() => submit(Number(target))}
              disabled={link.isPending || target === undefined}
            >
              Verknüpfen
            </Button>
          </div>
        </div>
      ) : !account.is_linked ? (
        <div className="mt-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Link2 className="h-4 w-4" />
            Mit vorhandenem Konto verknüpfen
          </Button>
        </div>
      ) : null}
    </li>
  );
}
